import instanceAxios from "@/Core/Client/AxiosConfig";
import { ReferToResponseDto } from "@/Dtos/refer-to-dto";
import cn from "@/utils/class-names";
import { IsNullOrWhiteSpace } from "@/utils/Utils";
import { useQuery } from "@tanstack/react-query";
import { isEmpty } from "lodash";
import { useEffect, useState } from "react";
import { MultiSelect, Select, SelectOption, Text } from "rizzui";

interface IReferToSelect<T> {
    title: string;
    error?;
    defaultValue?: string | string[];
    referToEndPoint: string;
    name: string;
    hide?: boolean;
    register?: any;
    setValue?;
    className?: string;
    onSelected?: (value: string | number | any[] | undefined) => void;
    SelectedRefer?: (
        value:
            | T
            | ReferToResponseDto
            | T[]
            | ReferToResponseDto[]
            | any[]
            | undefined,
    ) => void;
    filter?(p: ReferToResponseDto | T): boolean;
    find?(value: ReferToResponseDto | T): ReferToResponseDto | T | undefined;
    disabled?: boolean;
    multiple?: boolean;
    maxEntity?: number;
    formatOptionLabel?: (option: any) => string;
    formatOptionValue?: (option: any) => string | number;
}

// Styles
// const styles = {
//     multiValue: (base, state) => {
//         return state.data.isFixed ? { ...base, opacity: "0.5" } : base;
//     },

//     multiValueLabel: (base, state) => {
//         return state.data.isFixed
//             ? { ...base, color: "#626262", paddingRight: 6 }
//             : base;
//     },

//     multiValueRemove: (base, state) => {
//         return state.data.isFixed ? { ...base, display: "none" } : base;
//     },

//     option: (provided) => ({
//         ...provided,
//         fontSize: "14px",
//     }),
// };

// update the MultiSelection
const ReferToSelect = <T,>({
    title,
    error,
    name,
    setValue,
    onSelected,
    hide = false,
    className,
    defaultValue,
    SelectedRefer,
    filter,
    referToEndPoint,
    find,
    disabled,
    multiple = false,
    // maxEntity,
    formatOptionLabel,
    formatOptionValue,
}: IReferToSelect<T>) => {
    const [multipleEntity, setMultipleEntity] = useState<
        string[] | ReferToResponseDto[] | T[]
    >([]);

    const [selected, setSelected] = useState<
        SelectOption & (ReferToResponseDto | T)
    >();

    if (IsNullOrWhiteSpace(referToEndPoint))
        return <>Empty EndPoint, please check</>;

    if (hide) return <></>;

    const { data, isFetched, isLoading } = useQuery<ReferToResponseDto[] | T[]>(
        {
            queryKey: [referToEndPoint],
            queryFn: async () => {
                const response = await instanceAxios.get<
                    ReferToResponseDto[] | T[]
                >(referToEndPoint);

                if (response.data && response.data.length === 1) {
                    const value = response.data[0];
                    setSelected(value as any);
                    SelectedRefer?.(value as any);
                    onSelected?.((value as any)?.id ?? value);
                    setValue?.(name, (value as any)?.id ?? value);
                }

                return response.data;
            },
            staleTime: 60 * 1000, // 1 minutes
            refetchOnWindowFocus: false,
        },
    );

    useEffect(() => {
        if (defaultValue && !multiple) {
            const selectedItem = data?.find(
                (r) => (r as any).id == defaultValue,
            );

            if (selectedItem) {
                setSelected({
                    ...selectedItem,
                    value: (selectedItem as any).id,
                    label: (selectedItem as any).name,
                });
                SelectedRefer?.(selectedItem);
                setValue?.(name, defaultValue);
            }
        }

        if (defaultValue && multiple) {
            setMultipleEntity([...(defaultValue as string[])]);
            SelectedRefer?.(defaultValue as string[]);
            setValue?.(name, defaultValue);
        }

        const interval = setInterval(() => {
            if (find) {
                const _find = data?.find(find);

                setSelected(
                    _find && {
                        ..._find,
                        value: (_find as any).id,
                        label: (_find as any).name,
                    },
                );
                SelectedRefer?.(_find);
                setValue?.(name, _find);
            }
        }, 100);

        return () => clearInterval(interval); // Clean up the interval on component unmount
    }, [error, data, find, isLoading, isFetched]); // Empty array ensures this effect runs only once

    if (isLoading) return <Text>Loading...</Text>;

    if (isEmpty(data))
        return (
            <Text className="text-sm font-medium text-gray-900 capitalize">
                No data found {title}
            </Text>
        );

    return (
        <>
            {!multiple ? (
                <Select
                    label={title}
                    // defaultValue={defaultValue}
                    // inPortal={false}
                    className={cn(className)}
                    dropdownClassName="h-auto"
                    placeholder={`Select ${title}...`}
                    value={selected}
                    labelClassName="text-sm font-medium text-gray-900"
                    error={error}
                    disabled={disabled}
                    onChange={(e: SelectOption & (ReferToResponseDto | T)) => {
                        console.log("Selected ", e);

                        const value =
                            data?.find((r) => (r as any).id == e.value) ??
                            e.value;

                        if (value !== undefined && value !== null) {
                            setSelected(value as any);
                            SelectedRefer?.(value as any);
                            onSelected?.((value as any)?.id ?? value);
                            setValue?.(name, (value as any)?.id ?? value);
                        } else {
                            SelectedRefer?.(undefined);
                            setSelected(undefined);
                            onSelected?.(undefined);
                        }
                    }}
                    // {...register(name, {
                    //     required: true,
                    // })}
                    name={name}
                    options={
                        data?.filter(filter || (() => true)).map((op) => ({
                            ...op,
                            value: formatOptionValue
                                ? formatOptionValue(op)
                                : ((op as any)?.id?.toString() ?? op),
                            label: (formatOptionLabel
                                ? formatOptionLabel(op)
                                : ((op as any)?.name?.toString() ??
                                    op)) as string,
                        })) ?? []
                    }
                />
            ) : (
                <MultiSelect
                    name={name}
                    label={title}
                    error={error}
                    clearable={true}
                    searchable={true}
                    disabled={disabled}
                    className={cn("w-full max-w-md", className)}
                    // placement="bottom-end"
                    hideSelectedOptions={true}
                    dropdownClassName="max-w-md"
                    onClear={() => {
                        setMultipleEntity([]);
                        setValue?.(name, []);
                    }}
                    placeholder={`Select ${title}...`}
                    selectClassName="h-auto"
                    selectedItemClassName="border-primary border capitalize"
                    options={
                        data?.map((op) => ({
                            ...op,
                            value: op.id,
                            label: op.name,
                        })) ?? []
                    }
                    value={multipleEntity as string[]}
                    onChange={(value) => {
                        if (value !== undefined && value !== null) {
                            setMultipleEntity([...value]);
                            SelectedRefer?.(value as string[]);
                            onSelected?.(value as string[]);
                            setValue?.(name, [...value]);
                        } else {
                            SelectedRefer?.([]);
                            setMultipleEntity([]);
                            setValue?.(name, []);
                            onSelected?.([]);
                        }
                    }}
                />
            )}
        </>
    );
};

export default ReferToSelect;
